import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { userService } from '../../services'
import { useNavigate, useParams } from 'react-router-dom'
import PageLayout from '../../components/layout/page-layout'
import {
  FormContainer,
  FormInput,
  FormSelect,
  FormCheckbox,
  FormActions,
} from '../../components/forms'
import { Container } from '../../components/ui/container'

function maskEmail(email: string): string {
  const [local, domain] = email.split('@')
  if (!local || !domain) return email
  return `${local[0]}***@${domain}`
}

function maskPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '')
  if (digits.length <= 4) return phone
  return `•••• ${digits.slice(-4)}`
}

export default function EditUserPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()
  const queryClient = useQueryClient()

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    role: 'EMPLOYEE' as 'EMPLOYEE' | 'ADMIN',
    isActive: true,
    startDate: '',
    function: '' as '' | 'IT' | 'HR' | 'Lager' | 'Putzkraft' | 'Pflege',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()

  const { data: userData, isLoading } = useQuery({
    queryKey: ['user', id],
    queryFn: () => userService.getUserById(id!),
    enabled: !!id,
  })

  useEffect(() => {
    if (userData) {
      setFormData({
        email: userData.email || '',
        firstName: userData.firstName,
        lastName: userData.lastName,
        phoneNumber: userData.phoneNumber || '',
        role: userData.role as 'EMPLOYEE' | 'ADMIN',
        isActive: userData.isActive,
        startDate: userData.startDate || '',
        function: (userData.function as '' | 'IT' | 'HR' | 'Lager' | 'Putzkraft' | 'Pflege') || '',
      })
    }
  }, [userData])

  const updateUserMutation = useMutation({
    mutationFn: (userData: any) => userService.updateUser(id!, userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['company-users'] })
      queryClient.invalidateQueries({ queryKey: ['user', id] })
      navigate('/users')
    },
    onError: (error: any) => {
      setError(error?.response?.data?.message ?? t('users.updateUserError'))
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(undefined)

    if (formData.role === 'EMPLOYEE') {
      const hasEmail = !!formData.email?.trim()
      const hasPhone = !!formData.phoneNumber?.trim()
      if (!hasEmail && !hasPhone) {
        setError(t('users.lastLoginIdentity'))
        return
      }
    }

    setLoading(true)

    try {
      await updateUserMutation.mutateAsync(formData)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }))
  }

  const roleOptions = useMemo(
    () => [
      { value: 'EMPLOYEE', label: t('roles.employee') },
      { value: 'ADMIN', label: t('roles.admin') },
    ],
    [t]
  )

  const functionOptions = useMemo(
    () => [
      { value: '', label: t('common.noSelection') },
      { value: 'IT', label: t('employee.functions.it') },
      { value: 'HR', label: t('employee.functions.hr') },
      { value: 'Lager', label: t('employee.functions.warehouse') },
      { value: 'Putzkraft', label: t('employee.functions.cleaning') },
      { value: 'Pflege', label: t('employee.functions.care') },
    ],
    [t]
  )

  return (
    <PageLayout
      headerProps={{
        title: t('users.editUserTitle'),
        subtitle: t('users.editUserSubtitle', {
          firstName: userData?.firstName ?? '',
          lastName: userData?.lastName ?? '',
        }),
        variant: 'dashboard',
      }}
      isLoading={isLoading}
      error={updateUserMutation.error}
      loadingMessage={t('users.loadingUser')}
      errorMessage={error || t('users.updateUserError')}
    >
      <Container>
        <FormContainer
          title={t('users.editUserTitle')}
          subtitle={t('users.editUserSubtitle', {
            firstName: userData?.firstName ?? '',
            lastName: userData?.lastName ?? '',
          })}
          onSubmit={handleSubmit}
        >
          <div className='space-y-6'>
            {error && (
              <div className='p-4 bg-red-50 border border-red-200 rounded-xl'>
                <p className='text-red-600'>{error}</p>
              </div>
            )}

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <FormInput
                label={t('common.firstName')}
                name='firstName'
                value={formData.firstName}
                onChange={handleChange}
                placeholder='Max'
                required
              />

              <FormInput
                label={t('common.lastName')}
                name='lastName'
                value={formData.lastName}
                onChange={handleChange}
                placeholder='Mustermann'
                required
              />
            </div>

            <div className='rounded-xl border border-neutral-200 bg-neutral-50 p-4 space-y-2'>
              <p className='text-sm font-medium text-neutral-800'>{t('users.loginOptionsTitle')}</p>
              {userData?.loginEmail && (
                <p className='text-sm text-neutral-700'>
                  ✓ {t('common.email')}: {maskEmail(userData.loginEmail)}
                </p>
              )}
              {userData?.loginPhone && (
                <p className='text-sm text-neutral-700'>
                  ✓ {t('common.phone')}: {maskPhone(userData.loginPhone)}
                </p>
              )}
              {!userData?.loginEmail && !userData?.loginPhone && (
                <p className='text-sm text-neutral-500'>{t('users.noLoginIdentity')}</p>
              )}
            </div>

            <FormInput
              label={t('common.email')}
              name='email'
              type='email'
              value={formData.email}
              onChange={handleChange}
              placeholder={t('users.emailPlaceholder')}
              required={formData.role !== 'EMPLOYEE'}
              helperText={formData.role === 'EMPLOYEE' ? t('users.loginIdentityHelper') : undefined}
            />

            <FormInput
              label={t('common.phone')}
              name='phoneNumber'
              type='tel'
              value={formData.phoneNumber}
              onChange={handleChange}
              placeholder='+49 123 456789'
              helperText={formData.role === 'EMPLOYEE' ? t('users.loginIdentityHelper') : undefined}
            />

            {formData.role === 'EMPLOYEE' && (
              <p className='text-sm text-neutral-600 -mt-2'>{t('users.loginIdentityHint')}</p>
            )}

            <FormSelect
              label={t('common.role')}
              name='role'
              value={formData.role}
              onChange={handleChange}
              options={roleOptions}
              required
            />

            <FormInput
              label={t('common.startDate')}
              name='startDate'
              type='date'
              value={formData.startDate}
              onChange={handleChange}
            />

            <FormSelect
              label={t('common.function')}
              name='function'
              value={formData.function}
              onChange={handleChange}
              options={functionOptions}
            />

            <FormCheckbox
              label={t('employee.activeCheckbox')}
              name='isActive'
              checked={formData.isActive}
              onChange={handleChange}
            />

            <FormActions
              onCancel={() => navigate('/users')}
              submitText={t('common.saveChanges')}
              isLoading={loading || updateUserMutation.isPending}
            />
          </div>
        </FormContainer>
      </Container>
    </PageLayout>
  )
}
